import { buildPolarityById, POSITIVE, NEGATIVE, NEUTRAL } from "../sentiment";
import { CONFIG } from "./CONFIG";

// The demographic rollup dimension for aggrMetrics. The annotation carries the
// entity link denormalized on top-level `tags.<linkedDataset>`, so we roll that up
// to the demographic. isAnno omitted → BE reads top-level `tags` (entity link), not
// `annotations.tags` (attribute tags).
//   - categorical: terminal is a scalar ON the entity → [entity, main.<field>(.value)]
//   - tagRef: demographic is itself a link → one extra tag hop → [entity, refDataset]
export const buildRollupDimension = (linkedDataset, dim) => {
  if (!linkedDataset || !dim) return null;
  if (dim.kind === "tagRef") return { rollupType: "tagType", path: [linkedDataset, dim.refDataset] };
  const terminal = dim.saveValueAsString ? dim.valuePath : `${dim.valuePath}.value`;
  return { rollupType: "tagType", path: [linkedDataset, terminal] };
};

// Assemble the 2-dim aggrMetrics hierarchy into per-segment rows.
// Shape in: [{ value, display, count, children: [{ value: tagId, display, count }] }]
//   value/display = the segment (demographic value); children = its annotation tags.
// Out (per segment): mention total + top signals + sentiment breakdown, sorted by
// total, capped at CONFIG.maxSegments. Only segments with mentions are kept.
export const assembleSegments = (data, { signals = [], emotionAffect = [] } = {}, source = CONFIG.sentimentSource) => {
  const signalIds = new Set(signals.map((s) => String(s._id)));
  const polarityById = buildPolarityById(source, { signals, emotionAffect });

  return (data || [])
    .map((seg) => {
      const children = seg.children || [];

      // signals lens: the segment's signal tags, top-N by count
      const segSignals = children
        .filter((c) => signalIds.has(String(c.value)))
        .map((c) => ({ signal: c.display, signalId: String(c.value), count: c.count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, CONFIG.topSignals);

      // sentiment lens: roll the segment's emotion/signal tags into polarity
      const s = { [POSITIVE]: 0, [NEGATIVE]: 0, [NEUTRAL]: 0 };
      for (const c of children) {
        const pol = polarityById[String(c.value)];
        if (pol) s[pol] += c.count;
      }
      const classified = s[POSITIVE] + s[NEGATIVE] + s[NEUTRAL];
      const health = classified ? (s[POSITIVE] - s[NEGATIVE]) / classified : 0;

      return {
        segment: seg.display ?? seg.value,
        segmentValue: seg.value,
        total: seg.count,
        signals: segSignals,
        sentiment: { positive: s[POSITIVE], negative: s[NEGATIVE], neutral: s[NEUTRAL], total: classified, health },
      };
    })
    .filter((seg) => seg.total > 0)
    .sort((a, b) => b.total - a.total)
    .slice(0, CONFIG.maxSegments);
};
